<template>
    <div v-if="loaded">
        <card-header icon="fa-flag-swallowtail" :title="milestone.course_title"/>

        <card-body>
            <card-list>
                <!-- Student Information -->
                <subheader-list-item title="Student Information" icon="fa-user"/>
                <data-row title="Name">{{ milestone.first_name }} {{ milestone.last_name }}</data-row>
                <data-row title="Email">{{ milestone.email }}</data-row>

                <!-- Milestone Information -->
                <subheader-list-item title="Milestone Information" icon="fa-flag-swallowtail"/>
                <data-row title="Milestone">{{ milestone.milestone_number }} of {{ milestone.total_milestones }}</data-row>
                <data-row title="Hour Threshold">{{ getMilestoneRange() }}</data-row>
                <data-row title="Submitted">{{ milestone.submitted_at | moment('MMMM Do, YYYY [at] h:mm A') }}</data-row>
                <data-row v-if="isStem" title="STEM Clock Hours">
                    <span class="has-text-success">
                        <i class="fas fa-flask mr-1"></i> Opted in &mdash; reflections must address the OSPI STEM questions to count as STEM time
                    </span>
                </data-row>

                <!-- AI Instructions (what student was asked to do) -->
                <template v-if="milestone.instructions">
                    <subheader-list-item title="Instructions Given" icon="fa-clipboard-list"/>
                    <container-list-item padded>
                        <div class="ai-content" v-html="milestone.instructions"></div>
                    </container-list-item>
                </template>

                <!-- Student's Reflection -->
                <subheader-list-item title="Student's Reflection" icon="fa-pen-fancy"/>
                <container-list-item padded>
                    <p class="content-text">{{ milestone.reflection_text }}</p>
                </container-list-item>

                <!-- Study log sessions in this milestone's hour window -->
                <template v-if="milestone.study_log && milestone.study_log.session_count">
                    <subheader-list-item title="Study Log (this milestone)" icon="fa-clock"/>
                    <container-list-item padded>
                        <p class="is-size-7 has-text-grey mb-2">
                            {{ milestone.study_log.session_count }} session(s);
                            {{ formatLogMins(milestone.study_log.in_window_mins) }} of the window's
                            {{ formatLogMins(milestone.study_log.window_mins) }} logged<span v-if="milestone.study_log.stem_mins">,
                            including {{ formatLogMins(milestone.study_log.stem_mins) }} against STEM objectives</span>.
                        </p>
                        <div v-for="(e, i) in milestone.study_log.entries" :key="i" class="study-log-row">
                            <span class="has-text-weight-bold mr-2">{{ e.date | moment('MMM D, YYYY') }}</span>
                            {{ e.activity || 'Course session' }}
                            <span class="tag is-light is-size-7 ml-1">{{ formatLogMins(e.mins) }}</span>
                            <span v-if="e.stem" class="tag is-success is-light is-size-7 ml-1"><i class="fas fa-flask mr-1"></i>STEM</span>
                            <span v-if="e.starts_before_window || e.extends_beyond_window" class="has-text-grey is-size-7 ml-1">(spans milestone boundary)</span>
                        </div>
                        <p v-if="milestone.study_log.truncated_count" class="has-text-grey is-size-7 mt-1">&hellip;and {{ milestone.study_log.truncated_count }} more sessions</p>
                    </container-list-item>
                </template>

                <!-- AI Screening Assessment (informational) -->
                <template v-if="milestone.ai_review">
                    <subheader-list-item title="AI Assessment" icon="fa-robot"/>
                    <container-list-item padded>
                        <div class="mb-2">
                            <span class="tag" :class="aiVerdictClass">{{ aiVerdictLabel }}</span>
                            <span v-if="milestone.ai_review.confidence !== null" class="has-text-grey is-size-7 ml-2">
                                {{ aiConfidencePct }}% confidence
                            </span>
                            <span class="has-text-grey is-size-7 ml-2">{{ milestone.ai_review.created_at | moment('MMM D, h:mm A') }}</span>
                        </div>
                        <div v-for="(check, key) in aiChecks" :key="key" class="ai-check">
                            <i class="far fa-fw mr-1" :class="check.pass ? 'fa-check-circle has-text-success' : 'fa-times-circle has-text-danger'"></i>
                            <strong>{{ aiCheckLabels[key] || key }}</strong>
                            <span class="has-text-grey ml-2">{{ check.note }}</span>
                        </div>
                        <p v-if="milestone.ai_review.rationale" class="is-size-7 mt-2">{{ milestone.ai_review.rationale }}</p>
                        <template v-if="milestone.ai_review.student_feedback">
                            <label class="label is-size-7 has-text-grey mt-3 mb-1">Suggested feedback to student:</label>
                            <p class="content-text is-size-7">{{ milestone.ai_review.student_feedback }}</p>
                        </template>
                    </container-list-item>
                </template>

                <!-- Student's Attached Files -->
                <template v-if="milestone.files && milestone.files.length">
                    <subheader-list-item title="Attached Files" icon="fa-paperclip"/>
                    <container-list-item padded>
                        <div v-for="f in milestone.files" :key="f.id" class="attachment-row">
                            <a :href="f.preview_url || f.download_url" target="_blank" rel="noopener" :title="f.preview_url ? 'Preview in a new tab' : 'Download'">
                                <i class="far fa-fw fa-file mr-1"></i>{{ f.title }}
                            </a>
                            <span class="has-text-grey is-size-7 ml-2">{{ formatFileSize(f.size) }}</span>
                            <span class="has-text-grey is-size-7 ml-2">{{ f.created_at | moment('MMM D, YYYY') }}</span>
                            <a :href="f.download_url" class="ml-2" title="Download"><i class="far fa-download"></i></a>
                        </div>
                    </container-list-item>
                </template>

                <!-- Previous Milestones (collapsible history) -->
                <template v-if="previousMilestones.length > 0">
                    <subheader-list-item title="Previous Milestones" icon="fa-history"/>
                    <container-list-item padded>
                        <b-collapse
                            v-for="pm in previousMilestones"
                            :key="pm.id"
                            :open="false"
                            class="previous-milestone mb-3"
                        >
                            <template #trigger="props">
                                <div class="collapse-header">
                                    <i class="far fa-check-circle has-text-success mr-2"></i>
                                    <strong>Milestone {{ pm.milestone_number }}</strong>
                                    <span class="has-text-grey ml-2">({{ getPrevMilestoneRange(pm) }})</span>
                                    <i class="fas ml-auto" :class="props.open ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                                </div>
                            </template>
                            <div class="collapse-content mt-3">
                                <label class="label is-size-7 has-text-grey">Student's Reflection:</label>
                                <p class="content-text">{{ pm.reflection_text }}</p>

                                <div v-if="pm.files && pm.files.length" class="mt-3">
                                    <label class="label is-size-7 has-text-grey">Attached Files:</label>
                                    <div v-for="f in pm.files" :key="f.id" class="attachment-row">
                                        <a :href="f.preview_url || f.download_url" target="_blank" rel="noopener" :title="f.preview_url ? 'Preview in a new tab' : 'Download'">
                                            <i class="far fa-fw fa-file mr-1"></i>{{ f.title }}
                                        </a>
                                        <span class="has-text-grey is-size-7 ml-2">{{ formatFileSize(f.size) }}</span>
                                        <a :href="f.download_url" class="ml-2" title="Download"><i class="far fa-download"></i></a>
                                    </div>
                                </div>
                            </div>
                        </b-collapse>
                    </container-list-item>
                </template>

                <!-- AI Generation Section for Next Milestone -->
                <template v-if="hasNextMilestone">
                    <subheader-list-item title="Next Milestone Generation" icon="fa-robot"/>
                    <container-list-item padded>
                        <b-field label="Additional Guidance for Next Milestone (Optional)">
                            <b-input
                                type="textarea"
                                v-model="additionalGuidance"
                                placeholder="Provide any additional guidance for the AI to consider when generating the next milestone instructions..."
                                rows="3"
                            />
                        </b-field>

                        <div class="buttons mt-4">
                            <b-button
                                type="is-info"
                                size="is-small"
                                @click="generatePreview"
                                :loading="generating"
                                :disabled="generating"
                            >
                                <i class="fas fa-magic mr-2"></i>
                                {{ nextMilestonePreview ? 'Regenerate Preview' : 'Preview Next Milestone' }}
                            </b-button>
                        </div>

                        <!-- Preview of Next Milestone Instructions -->
                        <template v-if="nextMilestonePreview">
                            <label class="label is-size-7 has-text-grey mt-4">Preview for Milestone {{ milestone.milestone_number + 1 }}:</label>
                            <div class="ai-content preview-content" v-html="nextMilestonePreview"></div>
                        </template>
                    </container-list-item>
                </template>
            </card-list>
        </card-body>

        <card-footer>
            <div class="columns">
                <div class="column">
                    <b-button type="is-warning" size="is-small" @click="openReject">
                        <i class="fas fa-comment-dots mr-2"></i>
                        Request Changes
                    </b-button>
                </div>
                <div class="column has-text-right">
                    <b-button
                        type="is-success"
                        size="is-small"
                        @click="approve"
                        :loading="approving"
                    >
                        <i class="fas fa-check mr-2"></i>
                        Approve
                    </b-button>
                </div>
            </div>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardFooter from "@/TIER/components/CardFooter";
    import CardList from "@/TIER/components/CardList";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import DataRow from "@/TIER/components/DataRow";
    import {client as http} from "@/http_client";
    import {mapActions} from "vuex";

    export default {
        components: {DataRow, ContainerListItem, SubheaderListItem, CardList, CardFooter, CardBody, CardHeader},
        props: ['card', 'props'],
        data() {
            return {
                milestone: null,
                loaded: false,
                approving: false,
                generating: false,
                additionalGuidance: '',
                nextMilestonePreview: null
            };
        },
        computed: {
            previousMilestones() {
                if (!this.milestone || !this.milestone.all_milestones) return [];
                return this.milestone.all_milestones
                    .filter(m => m.milestone_number < this.milestone.milestone_number && m.status === 'approved')
                    .sort((a, b) => b.milestone_number - a.milestone_number);
            },
            hasNextMilestone() {
                if (!this.milestone || !this.milestone.all_milestones) return false;
                return this.milestone.all_milestones.some(m => m.milestone_number > this.milestone.milestone_number);
            },
            aiChecks() {
                return (this.milestone && this.milestone.ai_review && this.milestone.ai_review.checks) || {};
            },
            aiCheckLabels() {
                return {on_topic: 'On topic', specific: 'Specific', novel: 'New content', substantive: 'Substantive', stem_addressed: 'STEM addressed'};
            },
            isStem() {
                let setupData = this.milestone && this.milestone.setup_data;
                if (typeof setupData === 'string') {
                    try { setupData = JSON.parse(setupData); } catch (e) { setupData = null; }
                }
                return !!(setupData && setupData.stem_agreed === true);
            },
            aiConfidencePct() {
                return Math.round((this.milestone.ai_review.confidence || 0) * 100);
            },
            aiVerdictLabel() {
                const r = this.milestone.ai_review;
                if (r.action === 'skipped_resubmit') return 'Repeat submission';
                if (r.action === 'error') return 'Screening error';
                if (r.verdict === 'approve') return 'Leans approve';
                if (r.verdict === 'request_changes') return 'Leans request changes';
                return 'Needs human review';
            },
            aiVerdictClass() {
                const r = this.milestone.ai_review;
                if (r.action === 'error') return 'is-danger is-light';
                if (r.verdict === 'approve') return 'is-success is-light';
                if (r.verdict === 'request_changes') return 'is-warning is-light';
                return 'is-info is-light';
            }
        },
        methods: {
            ...mapActions(['getMilestonesCount']),
            getMilestoneRange() {
                if (!this.milestone || !this.milestone.all_milestones) return '';
                const prevThreshold = this.milestone.all_milestones
                    .filter(m => m.milestone_number < this.milestone.milestone_number)
                    .reduce((max, m) => Math.max(max, m.hour_threshold), 0);
                return `${prevThreshold}-${this.milestone.hour_threshold} hours`;
            },
            formatLogMins(mins) {
                if (!mins) return '0 min';
                if (mins < 60) return mins + ' min';
                return (Math.round((mins / 60) * 10) / 10) + ' h';
            },
            formatFileSize(bytes) {
                if (!bytes && bytes !== 0) return '';
                if (bytes < 1024) return bytes + ' B';
                if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
                return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
            },
            getPrevMilestoneRange(pm) {
                if (!this.milestone || !this.milestone.all_milestones) return '';
                const prevThreshold = this.milestone.all_milestones
                    .filter(m => m.milestone_number < pm.milestone_number)
                    .reduce((max, m) => Math.max(max, m.hour_threshold), 0);
                return `${prevThreshold}-${pm.hour_threshold} hours`;
            },
            load() {
                this.$emit('loading', true);
                http.get(`/milestones/${this.props.milestoneId}`, {force: true}).then(response => {
                    this.milestone = response.data;
                    this.loaded = true;
                    this.$emit('loading', false);
                }).catch(err => {
                    console.error('Error loading milestone:', err);
                    this.$emit('loading', false);
                    this.$buefy.toast.open({
                        message: 'Error loading milestone',
                        type: 'is-danger'
                    });
                });
            },
            generatePreview() {
                this.generating = true;
                http.post(`/milestones/${this.props.milestoneId}/regenerate`, {
                    additional_guidance: this.additionalGuidance
                }).then(response => {
                    this.nextMilestonePreview = response.data.instructions;
                    this.$buefy.toast.open({
                        message: 'Preview generated',
                        type: 'is-success'
                    });
                }).catch(err => {
                    console.error('Error generating preview:', err);
                    this.$buefy.toast.open({
                        message: err.response?.data?.error || 'Error generating preview',
                        type: 'is-danger'
                    });
                }).finally(() => {
                    this.generating = false;
                });
            },
            openReject() {
                this.$openCard('milestone-reject', {milestoneId: this.props.milestoneId}, this.card);
            },
            approve() {
                this.$buefy.dialog.confirm({
                    title: 'Approve Reflection',
                    message: 'Are you sure you want to approve this reflection? The student will be notified and the next milestone will be generated.',
                    confirmText: 'Approve',
                    type: 'is-success',
                    onConfirm: () => {
                        this.approving = true;
                        http.post(`/milestones/${this.props.milestoneId}/approve`, {
                            additional_guidance: this.additionalGuidance,
                            next_milestone_instructions: this.nextMilestonePreview
                        }).then(() => {
                            this.$buefy.toast.open({
                                message: 'Reflection approved',
                                type: 'is-success'
                            });
                            this.getMilestonesCount();
                            this.$closeCard(this.card);
                            this.$reloadCard('milestones');
                        }).catch(err => {
                            console.error('Error approving milestone:', err);
                            this.$buefy.toast.open({
                                message: 'Error approving reflection',
                                type: 'is-danger'
                            });
                        }).finally(() => {
                            this.approving = false;
                        });
                    }
                });
            }
        },
        watch: {
            'props.milestoneId': function() {
                this.load();
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<style lang="scss" scoped>
    .content-text {
        white-space: pre-wrap;
        line-height: 1.6;
    }

    .attachment-row {
        margin-bottom: 0.35rem;
    }

    .ai-check {
        font-size: 0.85rem;
        margin-bottom: 0.25rem;
    }

    .study-log-row {
        font-size: 0.85rem;
        margin-bottom: 0.25rem;
    }

    .ai-content {
        padding: 1rem;
        background: #fafafa;
        border-radius: 4px;

        ::v-deep {
            h4, h5 {
                margin-top: 1rem;
                margin-bottom: 0.5rem;
                color: #363636;
            }

            ul, ol {
                margin-left: 1.5rem;
                margin-bottom: 1rem;
            }

            li {
                margin-bottom: 0.25rem;
            }

            p {
                margin-bottom: 0.75rem;
            }
        }
    }

    .preview-content {
        border: 2px dashed #3273dc;
    }

    .previous-milestone {
        border: 1px solid #eee;
        border-radius: 4px;
        padding: 0.75rem;

        .collapse-header {
            display: flex;
            align-items: center;
            cursor: pointer;
        }

        .collapse-content {
            padding-left: 1.5rem;
            border-top: 1px solid #eee;
            padding-top: 0.75rem;
        }
    }
</style>
